import React, { useEffect, useRef, useState } from 'react';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogCancel } from '@/components/ui/alert-dialog';
import { registerConfirmHost } from '@/lib/confirmDelete';

const errText = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : (d?.message || 'That didn\'t work, so nothing was changed. Please try again.');
};

// Shared STP confirmation. onConfirm may be async: the dialog stays open (buttons locked) until it finishes,
// closes on success, and shows the error (record untouched) on failure. Escape/Cancel never confirm.
export function ConfirmDialog({ open, onOpenChange, title = 'Are you sure?', description, confirmLabel = 'Delete', onConfirm, destructive = true, busyLabel, tone }) {
  const dark = (tone || (window.location.pathname.startsWith('/my') ? 'portal' : 'coach')) === 'portal';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (open) { setBusy(false); setError(''); } }, [open]);
  const go = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try { await onConfirm?.(); onOpenChange?.(false); }
    catch (e) { setError(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange?.(o); }}>
      <AlertDialogContent className={dark ? 'rounded-2xl border-neutral-800 bg-neutral-900 text-neutral-100' : 'rounded-sm'} data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className={dark ? 'text-lg font-semibold' : 'display text-2xl'}>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription className={`text-sm whitespace-pre-line leading-relaxed ${dark ? 'text-neutral-400' : ''}`}>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        {error && <div className="text-sm text-red-700 border border-red-200 bg-red-50 rounded-sm px-3 py-2" role="alert" data-testid="confirm-error">{error}</div>}
        <AlertDialogFooter>
          <AlertDialogCancel className={dark ? 'rounded-full border-neutral-700 bg-transparent text-neutral-200 hover:bg-neutral-800' : 'rounded-sm'} disabled={busy} data-testid="confirm-cancel">Cancel</AlertDialogCancel>
          <button type="button" onClick={go} disabled={busy} data-testid="confirm-accept"
            className={`inline-flex items-center justify-center h-10 px-4 text-sm font-medium ${dark ? 'rounded-full' : 'rounded-sm'} disabled:opacity-60 ${destructive ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-primary text-primary-foreground hover:brightness-95'}`}>
            {busy ? (busyLabel || (destructive ? 'Deleting…' : 'Working…')) : confirmLabel}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Mounted once in App; lets any handler call confirmDelete({...}) without local dialog state.
export function ConfirmHost() {
  const [opts, setOpts] = useState(null);
  const done = useRef(false);
  const cur = useRef(null);
  useEffect(() => registerConfirmHost((o) => { if (cur.current && !done.current) cur.current.onCancel?.(); done.current = false; cur.current = o; setOpts(o); }), []);
  const close = () => { if (!done.current) cur.current?.onCancel?.(); done.current = false; cur.current = null; setOpts(null); };
  const onConfirm = async () => { await opts?.onConfirm?.(); done.current = true; };
  return <ConfirmDialog {...(opts || {})} onConfirm={onConfirm} open={!!opts} onOpenChange={(o) => !o && close()} />;
}
